"use client";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import { Users, ArrowRight, Loader2 } from "lucide-react";

const FALLBACK_DIRECT_RATE = "3%";
const FALLBACK_INDIRECT_RATE = "0.5%";

function fmtPercent(value, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return `${num}%`;
}

export default function Referral() {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [globalRates, setGlobalRates] = useState(null);
  /** null = unknown / still checking, true = signed in, false = guest. */
  const [isAuthed, setIsAuthed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/public/referral-ranks", { cache: "no-store" });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data?.rates) {
          setGlobalRates({
            directPercent: Number(data.rates.directPercent),
            indirectPercent: Number(data.rates.indirectPercent),
          });
        }
      } catch {
        /** Fallback rates take over via fmtPercent below. */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user/session", { credentials: "include" });
        if (cancelled) return;
        setIsAuthed(res.ok);
      } catch {
        if (!cancelled) setIsAuthed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Warm both targets so the click feels instant once we know the answer. */
  useEffect(() => {
    if (isAuthed === null) return;
    router.prefetch(isAuthed ? "/user/referral" : "/user");
  }, [isAuthed, router]);

  const ctaHref = isAuthed === true ? "/user/referral" : "/user";

  const handleStartReferring = useCallback(
    async (e) => {
      e.preventDefault();
      let authed = isAuthed;
      if (authed === null) {
        try {
          const res = await fetch("/api/user/session", { credentials: "include" });
          authed = res.ok;
        } catch {
          authed = false;
        }
        setIsAuthed(authed);
      }
      router.push(authed ? "/user/referral" : "/user");
    },
    [isAuthed, router]
  );

  const directRate = fmtPercent(globalRates?.directPercent, FALLBACK_DIRECT_RATE);
  const indirectRate = fmtPercent(globalRates?.indirectPercent, FALLBACK_INDIRECT_RATE);

  const commissionCards = [
    {
      title: "Direct Commission",
      rate: directRate,
      desc: `Earn ${directRate} on every deposit made by users you directly refer.`,
      color: "#F59E0B",
    },
    {
      title: "Indirect Commission",
      rate: indirectRate,
      desc: `Earn ${indirectRate} on deposits made by your referrals' referrals (2nd level).`,
      color: "#EA580C",
    },
  ];

  return (
    <section
      id="referral"
      className="bg-bg-secondary 2xl:px-6 xl:px-5 lg:px-4 md:px-4  px-4 2xl:py-24 xl:py-20 lg:py-16 md:py-12 py-10"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="mb-4 inline-block rounded-full border border-[rgba(245,158,11,0.25)] bg-[rgba(245,158,11,0.1)] px-4 py-1.5 text-xs font-medium text-accent-amber sm:text-sm">
            Referral Program
          </span>
          <h2 className="font-display mb-3 text-[clamp(32px,5vw,56px)]">
            Earn by <span className="text-ember">Referring</span>
          </h2>
          <p className="mx-auto max-w-[560px] text-base text-text-muted sm:text-lg">
            Build your network and earn commission on every deposit your
            referrals make — and theirs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {loading
            ? [0, 1].map((i) => (
                <div
                  key={i}
                  className="card-base flex min-h-[140px] items-center justify-center gap-3 p-4 2xl:p-8 xl:p-8 lg:p-7 md:p-6 sm:p-4"
                >
                  <Loader2 className="h-7 w-7 shrink-0 animate-spin text-accent-amber" aria-hidden />
                  <span className="text-sm text-text-muted">Loading commission rates…</span>
                </div>
              ))
            : commissionCards.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card-base flex items-start gap-5 p-4 2xl:p-8 xl:p-8 lg:p-7 md:p-6 sm:p-4"
                >
                  <div
                    style={{
                      background: `${item.color}18`,
                      border: `1px solid ${item.color}30`,
                    }}
                    className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl"
                  >
                    <Users size={22} color={item.color} />
                  </div>
                  <div>
                    <div
                      className="font-display mb-1 text-[28px] font-extrabold"
                      style={{ color: item.color }}
                    >
                      {item.rate}
                    </div>
                    <div className="mb-2 text-[15px] font-semibold">{item.title}</div>
                    <div className="text-sm leading-relaxed text-text-muted">{item.desc}</div>
                  </div>
                </motion.div>
              ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <a
            href={ctaHref}
            onClick={handleStartReferring}
            className="inline-flex items-center gap-2 rounded-xl bg-linear-to-br from-accent-amber to-accent-orange px-9 py-4 font-display text-base font-bold text-white shadow-[0_8px_30px_rgba(245,158,11,0.25)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(245,158,11,0.35)]"
          >
            Start Referring <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
